import { HYDRATION_REWRITE_MONITOR_SCRIPT } from './hydration-rewrite-monitor';
import type { SsrTrace } from './ssr-trace';

/** The deployment values the bootstrap stashes on `window` for the client to read back. */
export interface BootstrapRuntimeEnv {
	readonly cdnUrl: string;
	readonly imgproxyUrl: string;
	readonly analyticsCode: string;
	readonly recaptchaSiteKey: string;
	readonly adyenClientKey: string;
	readonly usercentricsSettingsId: string;
	/** `'true'` on every stage but `pro`; see the `data-draft` note in `loadUsercentrics`. */
	readonly usercentricsDraft: string;
}

/**
 * The trace this document was rendered in, as an inline script.
 *
 * Emitted even when there is no trace, as empty ids. The client rebuilds this text from the values it
 * reads back off `window`, so both cases round-trip byte-identically: real ids come back as themselves,
 * and empty ids come back as empty (`currentDocumentTrace` treats empty as absent, and this writes the
 * same empty strings again). Never made conditional — see {@link bootstrapHeadScripts}.
 */
function initTraceScript(trace: SsrTrace | undefined): string {
	return (
		`window.__TRACE_ID=${JSON.stringify(trace?.traceId ?? '')};` +
		`window.__ROOT_SPAN_ID=${JSON.stringify(trace?.rootSpanId ?? '')};`
	);
}

/**
 * Defines `globalThis.__assetUrl` for Vite's `renderBuiltUrl` runtime form (see vite.config.ts) and
 * stashes the deployment env vars on `window` so the rest of the app (router, image, recaptcha, adyen)
 * resolves them at runtime instead of build time. Must execute before the entry module loads any chunk.
 *
 * Its text has to stay byte-identical between server and client: TanStack's `Script` dedups by
 * comparing the text it renders against the inline scripts already in the document, so any divergence
 * makes the comparison fail and the whole bootstrap is re-injected and re-executed on every hydration.
 * That is why the per-request trace ids get their own script rather than being folded in here.
 */
function initAssetUrlScript(env: BootstrapRuntimeEnv): string {
	return (
		`window.__CDN_URL=${JSON.stringify(env.cdnUrl)};` +
		`window.__IMGPROXY_URL=${JSON.stringify(env.imgproxyUrl)};` +
		`window.__ANALYTICS_CODE=${JSON.stringify(env.analyticsCode)};` +
		`window.__GOOGLE_RECAPTCHA_SITE_KEY=${JSON.stringify(env.recaptchaSiteKey)};` +
		`window.__ADYEN_CLIENT_KEY=${JSON.stringify(env.adyenClientKey)};` +
		`window.__USERCENTRICS_SETTINGS_ID=${JSON.stringify(env.usercentricsSettingsId)};` +
		`window.__USERCENTRICS_DRAFT=${JSON.stringify(env.usercentricsDraft)};` +
		`globalThis.__assetUrl=function(f){var b=window.__CDN_URL||'';return (b?b.replace(/\\/$/,'')+'/':'/')+f}`
	);
}

/**
 * Google Consent Mode v2 default states: everything denied until the donor says otherwise.
 *
 * Required of the WEBSITE, not the CMP. Usercentrics' Consent Mode feature is enabled for this
 * configuration and emits the `ads_data_redaction` / developer-id signals and the `consent` `update`
 * once the donor answers, but its own panel states: "Make sure to implement the consent mode script
 * including the default states on your website." Without this, gtag.js initialises with Google's
 * built-in default — granted — so GA measures the whole first visit before the banner is answered.
 *
 * Must be queued before gtag.js reads it. That is guaranteed here rather than merely likely: this runs
 * in the head pre-hydration, while `loadGtag` injects the tag from an effect after hydration commits.
 *
 * Takes no arguments on purpose — a constant string round-trips byte-identically between server and
 * client for free, which is the invariant {@link bootstrapHeadScripts} exists to protect.
 *
 * Note this does NOT stop the tag loading, and it should not: under Consent Mode the tag is meant to
 * load and self-restrict, which is what preserves cookieless pings and conversion modelling. Denying
 * storage is the mechanism; blocking the script is not.
 */
function initConsentModeScript(): string {
	return (
		'window.dataLayer=window.dataLayer||[];' +
		'window.gtag=window.gtag||function(){window.dataLayer.push(arguments)};' +
		'window.gtag("consent","default",{' +
		'ad_storage:"denied",' +
		'ad_user_data:"denied",' +
		'ad_personalization:"denied",' +
		'analytics_storage:"denied",' +
		// Hold measurement briefly so an early event waits for the donor's answer instead of being
		// recorded denied. This window has to cover the CMP's whole arrival, and that chain is longer here
		// than the usual 500-2000ms advice assumes: these defaults are queued during head parse, but
		// `loadUsercentrics` only fires from an effect AFTER hydration, and then has to fetch `loader.js`,
		// fetch the CMP itself, and restore the stored decision before it can emit the `update`. Sized for
		// that, not for a head-loaded CMP. Longer would hold more events hostage to a donor who bounces, so
		// this is the compromise rather than a measured optimum — worth checking against real timings.
		'wait_for_update:3000' +
		'});'
	);
}

/**
 * The inline scripts the document head bootstraps with, in execution order.
 *
 * **Every entry must stay INLINE (hence `string[]`) and unconditional.** Both properties are what keep
 * the head immune to a hydration mismatch, and the reason is specific to how React hydrates `<head>`:
 *
 * - TanStack renders an inline script as `dangerouslySetInnerHTML` + `suppressHydrationWarning`, so
 *   React neither compares its text (the text check short-circuits on `suppressHydrationWarning`) nor
 *   runs its leftover-children check (`shouldSetTextContent` is true for `dangerouslySetInnerHTML`).
 *   An inline script therefore absorbs any positional shift silently.
 * - A script with a `src` and no children gets neither exemption: `shouldSetTextContent` is false, so
 *   React runs the leftover-children check on it. Pair it with an inline script — which has a text
 *   node — and that text node is an unconsumed child, so React throws #418 `args[]=HTML` against a
 *   `<script>` inside `<head>` and regenerates the tree.
 * - `<head>` itself is a HostSingleton, which React excludes from that check, so leftovers sitting
 *   directly in the head are ignored. Only a shift that lands a text-bearing inline script in a
 *   childless script's position can raise the error.
 *
 * That is exactly what reached production on five builds: something inserted a `<script>` into the
 * head ahead of these (Instagram's in-app WebView in most of the affected sessions, which injects its
 * own JS), every rendered script paired with the wrong DOM element, the three inline ones stayed quiet,
 * and the fourth — the Google Analytics tag, the only one with a `src` and no children — raised #418.
 * Keeping this list inline-only means head injection can no longer produce a hydration error at all;
 * the analytics tag now loads after hydration instead, see `loadGtag`.
 *
 * Unconditional matters for a second, independent reason: the conditions once guarding these entries
 * were read from the `window` stash these very scripts write, so anything stopping one from taking
 * effect flipped a condition and dropped an entry on the client only — the same shift, self-inflicted.
 *
 * Order matters: the trace ids first so they are set before any chunk runs, then the runtime env, then
 * the Consent Mode defaults, then the rewrite recorder — which must stay LAST because it has to be
 * ahead of the entry module, and that module is `async` and on a slow device runs after a page
 * translator has already rewritten the text. Note the recorder watches `characterData` only, so it
 * cannot witness an injected script; a `replacements=0` report does not mean the head was untouched.
 */
export function bootstrapHeadScripts(env: BootstrapRuntimeEnv, trace: SsrTrace | undefined): readonly string[] {
	return [initTraceScript(trace), initAssetUrlScript(env), initConsentModeScript(), HYDRATION_REWRITE_MONITOR_SCRIPT];
}
